import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  Divider,
  IconButton,
  Stack,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { type ApproveImportSubmission, type InvoiceImportStatusDto } from '../Types/invoiceImport.types';
import { UserInvoiceCorrections } from '../Types/userInvoiceCorrections';
import ImportFa3SemanticPreview from './ImportFa3SemanticPreview';
import InvoiceDirectionBadge from './InvoiceDirectionBadge';
import {
  getInvoiceDirectionDecision,
  getMatchedSubjectRoleLabel,
} from '../Utils/invoiceDirection';
import { mapOcrFieldToFormField } from '../Utils/ocrFieldNameMapping';

/**
 * Validation schema matching backend rules (INVOICE_IMPORT_PHASE_4_BACKEND_RECOMMENDATIONS.md section 7)
 * - Max 100 items
 * - NIP regex: ^[0-9A-Za-z]{5,20}$
 * - ISO 4217 currency (PLN, EUR, USD)
 * - Quantity: 1-100000
 * - UnitNet/Gross: 0-1000000
 * - Max text lengths: 256 for standard fields, 1000 for comments
 */
const correctionSchema = yup.object({
  invoiceNumber: yup.string().max(256, 'Max 256 znaków').optional(),
  issueDate: yup
    .string()
    .matches(/^\d{4}-\d{2}-\d{2}$/, {
      message: 'Format daty: RRRR-MM-DD',
      excludeEmptyString: true,
    })
    .optional(),
  sellerName: yup.string().max(256, 'Max 256 znaków').optional(),
  sellerAddress: yup.string().max(256, 'Max 256 znaków').optional(),
  sellerNip: yup
    .string()
    .matches(/^[0-9A-Za-z]{5,20}$/, 'NIP: 5-20 znaków alfanumerycznych')
    .optional(),
  sellerBankAccount: yup
    .string()
    .test(
      'max-iban-length',
      'Max 34 znaki (IBAN)',
      (value) => !value || value.replace(/\s/g, '').length <= 34
    )
    .optional(),
  buyerName: yup.string().max(256, 'Max 256 znaków').optional(),
  buyerAddress: yup.string().max(256, 'Max 256 znaków').optional(),
  buyerNip: yup
    .string()
    .matches(/^[0-9A-Za-z]{5,20}$/, 'NIP: 5-20 znaków alfanumerycznych')
    .optional(),
  recipientName: yup.string().max(256, 'Max 256 znaków').optional(),
  recipientAddress: yup.string().max(256, 'Max 256 znaków').optional(),
  recipientNip: yup
    .string()
    .matches(/^[0-9A-Za-z]{5,20}$/, 'NIP: 5-20 znaków alfanumerycznych')
    .optional(),
  currency: yup
    .string()
    .matches(/^[A-Z]{3}$/, 'Kod waluty ISO 4217 (np. PLN, EUR, USD)')
    .optional(),
  totalNet: yup.number().min(-1000000, 'Min: -1 000 000').max(1000000, 'Max: 1 000 000').optional(),
  totalVat: yup.number().min(-1000000, 'Min: -1 000 000').max(1000000, 'Max: 1 000 000').optional(),
  totalGross: yup.number().min(-1000000, 'Min: -1 000 000').max(1000000, 'Max: 1 000 000').optional(),
  currencyRate: yup.number().min(0.0001, 'Min: 0.0001').max(1000, 'Max: 1000').optional(),
  comments: yup.string().max(1000, 'Max 1000 znaków').optional(),
  legalNote: yup.string().max(1000, 'Max 1000 znaków').optional(),
  reviewNote: yup.string().max(1000, 'Max 1000 znaków').optional(),
  items: yup
    .array()
    .of(
      yup.object({
        name: yup.string().required('Nazwa pozycji wymagana').max(256, 'Max 256 znaków'),
        quantity: yup
          .number()
          .required('Ilość wymagana')
          .min(1, 'Min: 1')
          .max(100000, 'Max: 100000'),
        unitNet: yup
          .number()
          .required('Cena netto wymagana')
          // Signed bound (S7 D15): final-invoice advance-deduction lines are legitimately
          // negative. Whether a negative line is actually legal is decided by the backend
          // semantic policy after metadata-preserving merge, not by this form.
          .min(-1000000, 'Min: -1 000 000')
          .max(1000000, 'Max: 1 000 000'),
        unitGross: yup.number().min(-1000000, 'Min: -1 000 000').max(1000000, 'Max: 1 000 000').optional(),
        vatRate: yup.number().min(0, 'Min: 0').max(1, 'Max: 1 (100%)').optional(),
        isVatExempt: yup.boolean().optional(),
        vatExemptNote: yup.string().max(256, 'Max 256 znaków').optional(),
        sourceIndex: yup.number().nullable().optional(),
      })
    )
    .optional(),
});

type CorrectionFormData = yup.InferType<typeof correctionSchema>;

interface ApproveImportModalProps {
  open: boolean;
  onClose: () => void;
  importStatus: InvoiceImportStatusDto | null;
  initialCorrections?: UserInvoiceCorrections | null;
  onApprove: (submission: ApproveImportSubmission) => void;
  isLoading?: boolean;
}

/**
 * Modal for approving invoice import with optional user corrections
 * Implements Phase 4 Manual Review workflow
 * Reference: docs/invoice-import/INVOICE_IMPORT_PHASE_4_BACKEND_RECOMMENDATIONS.md
 */
const ApproveImportModal: React.FC<ApproveImportModalProps> = ({
  open,
  onClose,
  importStatus,
  initialCorrections,
  onApprove,
  isLoading = false,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CorrectionFormData>({
    resolver: yupResolver(correctionSchema) as any, // Yup schema complex type inference workaround
    defaultValues: {
      invoiceNumber: undefined,
      issueDate: undefined,
      sellerName: undefined,
      sellerAddress: undefined,
      sellerNip: undefined,
      sellerBankAccount: undefined,
      buyerName: undefined,
      buyerAddress: undefined,
      buyerNip: undefined,
      recipientName: undefined,
      recipientAddress: undefined,
      recipientNip: undefined,
      currency: undefined,
      totalNet: undefined,
      totalVat: undefined,
      totalGross: undefined,
      currencyRate: undefined,
      comments: undefined,
      legalNote: undefined,
      reviewNote: undefined,
      items: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });
  const [overrideConfirmed, setOverrideConfirmed] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const invoiceDirection = importStatus?.invoiceDirection ?? 'unknown';
  const directionDecision = getInvoiceDirectionDecision(invoiceDirection, importStatus?.importerType);
  const isApproveDirectionAllowed = directionDecision.canOpenApproveModal;
  const requiresUnknownOverride = directionDecision.requiresOverride;
  const isOverrideReady =
    !requiresUnknownOverride || (overrideConfirmed && overrideReason.trim().length > 0);
  const isApproveEnabled = isApproveDirectionAllowed && isOverrideReady;
  // "Zatwierdź bez zmian" submits no corrections, so ApplyCorrections is a no-op and the merged
  // data the backend re-validates is identical to the raw, uncorrected data allowedActions was
  // computed from (RM-09). Unlike "Zatwierdź z korektami" or opening this modal, there is no
  // correction that could change that outcome, so gating this button on allowedActions is safe.
  const canApproveWithoutCorrections =
    isApproveEnabled && (importStatus?.allowedActions?.includes('approve') ?? false);
  const matchedSubjectRoleLabel = importStatus?.matchedSubjectRole
    ? getMatchedSubjectRoleLabel(importStatus.matchedSubjectRole)
    : null;

  // Highlights the specific correction field an OCR low-confidence issue points at (R3). Reads
  // directly from importStatus.issues on every render — purely presentational, does not feed
  // into RHF state, so it never triggers reset()/defaultValues changes (I3). `message` is
  // already the RM-07 allowlisted string resolved by invoiceImportResponseMapper, never raw
  // backend text (I4).
  const lowConfidenceFieldMessages = new Map<string, string>();
  (importStatus?.issues ?? []).forEach((issue) => {
    if (issue.code !== 'validationLowFieldConfidence') {
      return;
    }
    const formField = mapOcrFieldToFormField(issue.field);
    if (formField) {
      lowConfidenceFieldMessages.set(formField, issue.message);
    }
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    setOverrideConfirmed(false);
    setOverrideReason('');
  }, [open, importStatus?.importJobId, invoiceDirection]);

  const buildApproveSubmission = (
    userCorrections: UserInvoiceCorrections | null
  ): ApproveImportSubmission => {
    if (!requiresUnknownOverride) {
      return {
        userCorrections,
      };
    }

    return {
      userCorrections,
      approveOverride: true,
      approveOverrideReason: overrideReason.trim(),
    };
  };

  // Reset form when modal opens with parsed data as defaults
  useEffect(() => {
    if (open && importStatus?.parsedInvoiceData) {
      const parsed = importStatus.parsedInvoiceData;

      // S7 (D13/D15): every existing line carries the zero-based sourceIndex of its parsed-item
      // origin, so indexed replacement can clone provider-neutral metadata by index on submit.
      // Reopening with a prior correction session preserves whatever sourceIndex it already
      // carried instead of re-deriving it from the current array position.
      const itemsFromInitial =
        initialCorrections?.items && initialCorrections.items.length > 0
          ? initialCorrections.items.map((item) => ({
              name: item.name || '',
              quantity: item.quantity || 1,
              unitNet: item.unitNet || 0,
              unitGross: item.unitGross || undefined,
              vatRate: item.vatRate || undefined,
              isVatExempt: item.isVatExempt || false,
              vatExemptNote: item.vatExemptNote || undefined,
              sourceIndex: item.sourceIndex ?? null,
            }))
          : null;

      reset({
        invoiceNumber: initialCorrections?.invoiceNumber || parsed.invoiceNumber || undefined,
        issueDate: initialCorrections?.issueDate || parsed.issueDate || undefined,
        sellerName: initialCorrections?.sellerName || parsed.sellerName || undefined,
        sellerAddress: parsed.sellerAddress || undefined,
        sellerNip: initialCorrections?.sellerNip || parsed.sellerNip || undefined,
        sellerBankAccount: parsed.sellerBankAccount || undefined,
        buyerName: initialCorrections?.buyerName || parsed.buyerName || undefined,
        buyerAddress: parsed.buyerAddress || undefined,
        buyerNip: initialCorrections?.buyerNip || parsed.buyerNip || undefined,
        recipientName: parsed.recipientName || undefined,
        recipientAddress: parsed.recipientAddress || undefined,
        recipientNip: parsed.recipientNip || undefined,
        currency: initialCorrections?.currency?.trim().toUpperCase() || parsed.currency || undefined,
        totalNet: initialCorrections?.totalNet ?? parsed.totalNet ?? undefined,
        totalVat: initialCorrections?.totalVat ?? parsed.totalVat ?? undefined,
        totalGross: initialCorrections?.totalGross ?? parsed.totalGross ?? undefined,
        currencyRate: initialCorrections?.currencyRate ?? parsed.currencyRate ?? undefined,
        comments: parsed.comments || undefined,
        legalNote: parsed.legalNote || undefined,
        reviewNote: initialCorrections?.notes || undefined,
        items:
          itemsFromInitial ||
          parsed.items?.map((item, index) => ({
            name: item.name || '',
            quantity: item.quantity || 1,
            unitNet: item.unitNet || 0,
            unitGross: item.unitGross || undefined,
            vatRate: item.vatRate || undefined,
            isVatExempt: item.isVatExempt || false,
            vatExemptNote: item.vatExemptNote || undefined,
            sourceIndex: index,
          })) ||
          [],
      });
    }
  }, [open, importStatus, initialCorrections, reset]);

  const onSubmit = (data: CorrectionFormData) => {
    if (!isApproveEnabled) {
      return;
    }

    // Trim all string fields and convert to backend DTO. S7: always submit indexedReplacement
    // with the complete ordered item list (never omitted) so a delete-all is not lost as an
    // empty diff, and every retained line carries its sourceIndex for metadata-preserving merge.
    const corrections: UserInvoiceCorrections = {
      invoiceNumber: data.invoiceNumber?.trim() || undefined,
      issueDate: data.issueDate?.trim() || undefined,
      sellerName: data.sellerName?.trim() || undefined,
      sellerNip: data.sellerNip?.trim().replace(/[\s-]/g, '') || undefined, // Normalize NIP
      sellerBankAccount: data.sellerBankAccount?.trim().replace(/\s/g, '') || undefined, // Normalize IBAN
      buyerName: data.buyerName?.trim() || undefined,
      buyerNip: data.buyerNip?.trim().replace(/[\s-]/g, '') || undefined,
      currency: data.currency?.trim().toUpperCase() || undefined,
      totalNet: data.totalNet ?? undefined,
      totalVat: data.totalVat ?? undefined,
      totalGross: data.totalGross ?? undefined,
      currencyRate: data.currencyRate ?? undefined,
      notes: data.reviewNote?.trim() || undefined,
      itemsMode: 'indexedReplacement',
      items: (data.items ?? []).map((item) => ({
        name: item.name.trim(),
        quantity: item.quantity,
        unitNet: item.unitNet,
        unitGross: item.unitGross,
        vatRate: item.vatRate,
        isVatExempt: item.isVatExempt,
        vatExemptNote: item.vatExemptNote?.trim(),
        sourceIndex: item.sourceIndex ?? null,
      })),
    };

    onApprove(buildApproveSubmission(corrections));
  };

  const handleApproveWithoutCorrections = () => {
    if (!canApproveWithoutCorrections) {
      return;
    }

    onApprove(buildApproveSubmission(null));
  };

  const handleAddItem = () => {
    // S7 (D13): a new line has no parsed-item origin, so sourceIndex stays null — the backend
    // creates a new line with no fabricated OCR metadata for a null sourceIndex.
    append({
      name: '',
      quantity: 1,
      unitNet: 0,
      unitGross: undefined,
      vatRate: undefined,
      isVatExempt: false,
      vatExemptNote: undefined,
      sourceIndex: null,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          Weryfikacja i zatwierdzenie importu
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Plik: {importStatus?.fileName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75 }}>
            Kierunek dokumentu
          </Typography>
          <Stack spacing={0.75}>
            <InvoiceDirectionBadge
              direction={invoiceDirection}
              source={importStatus?.invoiceDirectionSource ?? 'derivedRuntime'}
            />
            <Typography variant="caption" color="text.secondary">
              {directionDecision.message}
            </Typography>
            {matchedSubjectRoleLabel ? (
              <Typography variant="caption" color="text.secondary">
                Dopasowana rola: {matchedSubjectRoleLabel}
              </Typography>
            ) : null}
            {importStatus?.invoiceDirectionReason ? (
              <Typography variant="caption" color="text.secondary">
                Powód klasyfikacji: {importStatus.invoiceDirectionReason}
              </Typography>
            ) : null}
          </Stack>
        </Box>

        <Alert
          severity={
            directionDecision.canOpenApproveModal
              ? requiresUnknownOverride
                ? 'warning'
                : 'info'
              : directionDecision.severity
          }
          sx={{ mb: 3 }}
        >
          {directionDecision.canOpenApproveModal && !requiresUnknownOverride
            ? 'Możesz zatwierdzić import bez zmian lub wprowadzić korekty. Puste pola zachowają oryginalne wartości z OCR/parsowania.'
            : directionDecision.message}
        </Alert>

        {requiresUnknownOverride ? (
          <Box sx={{ mb: 3 }}>
            <Stack spacing={1.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={overrideConfirmed}
                    onChange={(event) => setOverrideConfirmed(event.target.checked)}
                  />
                }
                label="Potwierdzam override dla kierunku unknown"
              />
              <TextField
                label="Powód override (wymagane)"
                value={overrideReason}
                onChange={(event) => setOverrideReason(event.target.value)}
                fullWidth
                multiline
                minRows={2}
                required
                error={overrideConfirmed && overrideReason.trim().length === 0}
                helperText={
                  overrideConfirmed && overrideReason.trim().length === 0
                    ? 'Podaj powód override, aby kontynuować zatwierdzenie.'
                    : 'Powód zostanie przekazany do audytu approve override.'
                }
              />
            </Stack>
          </Box>
        ) : null}

        <ImportFa3SemanticPreview
          preview={importStatus?.fa3SemanticPreview}
          parserMetadata={importStatus?.parsedInvoiceData?.parserMetadata}
          title="Semantyka FA(3) do weryfikacji"
        />

        <form
          id="approve-import-form"
          onSubmit={(event) => {
            // Defensive guard: prevents this Dialog's form submit from bubbling into
            // any future outer <form> ancestor in the React tree (see BatchInvoiceConfigModal).
            event.stopPropagation();
            return handleSubmit(onSubmit)(event);
          }}
        >
          {/* Invoice Identification Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Dane faktury
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="invoiceNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Numer faktury"
                      fullWidth
                      error={!!errors.invoiceNumber || lowConfidenceFieldMessages.has('invoiceNumber')}
                      helperText={errors.invoiceNumber?.message ?? lowConfidenceFieldMessages.get('invoiceNumber')}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="issueDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Data wystawienia"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.issueDate || lowConfidenceFieldMessages.has('issueDate')}
                      helperText={errors.issueDate?.message ?? lowConfidenceFieldMessages.get('issueDate')}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Seller Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Sprzedawca
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="sellerName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Nazwa sprzedawcy"
                      fullWidth
                      error={!!errors.sellerName || lowConfidenceFieldMessages.has('sellerName')}
                      helperText={errors.sellerName?.message ?? lowConfidenceFieldMessages.get('sellerName')}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="sellerNip"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="NIP sprzedawcy"
                      fullWidth
                      error={!!errors.sellerNip || lowConfidenceFieldMessages.has('sellerNip')}
                      helperText={errors.sellerNip?.message ?? lowConfidenceFieldMessages.get('sellerNip')}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="sellerAddress"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Adres sprzedawcy"
                      fullWidth
                      multiline
                      rows={2}
                      error={!!errors.sellerAddress}
                      helperText={errors.sellerAddress?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="sellerBankAccount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Nr konta bankowego"
                      fullWidth
                      error={!!errors.sellerBankAccount}
                      helperText={errors.sellerBankAccount?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Buyer Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Nabywca
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="buyerName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Nazwa nabywcy"
                      fullWidth
                      error={!!errors.buyerName}
                      helperText={errors.buyerName?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="buyerNip"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="NIP nabywcy"
                      fullWidth
                      error={!!errors.buyerNip}
                      helperText={errors.buyerNip?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }} >
                <Controller
                  name="buyerAddress"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Adres nabywcy"
                      fullWidth
                      multiline
                      rows={2}
                      error={!!errors.buyerAddress}
                      helperText={errors.buyerAddress?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Recipient Section (optional) */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Odbiorca (opcjonalnie)
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="recipientName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Nazwa odbiorcy"
                      fullWidth
                      error={!!errors.recipientName}
                      helperText={errors.recipientName?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="recipientNip"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="NIP odbiorcy"
                      fullWidth
                      error={!!errors.recipientNip}
                      helperText={errors.recipientNip?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }} >
                <Controller
                  name="recipientAddress"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Adres odbiorcy"
                      fullWidth
                      multiline
                      rows={2}
                      error={!!errors.recipientAddress}
                      helperText={errors.recipientAddress?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Items Section */}
          <Box sx={{ mb: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">Pozycje faktury ({fields.length}/100)</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                disabled={fields.length >= 100 || isLoading}
              >
                Dodaj pozycję
              </Button>
            </Stack>

            {errors.items && !Array.isArray(errors.items) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.items.message}
              </Alert>
            )}

            {fields.map((field, index) => (
              <Box
                key={field.id}
                sx={{
                  p: 2,
                  mb: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.default',
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle2">Pozycja #{index + 1}</Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => remove(index)}
                    aria-label="Usuń pozycję"
                    disabled={isLoading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }} >
                    <Controller
                      name={`items.${index}.name`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nazwa pozycji *"
                          fullWidth
                          error={!!errors.items?.[index]?.name}
                          helperText={errors.items?.[index]?.name?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }} >
                    <Controller
                      name={`items.${index}.quantity`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          label="Ilość *"
                          fullWidth
                          error={!!errors.items?.[index]?.quantity}
                          helperText={errors.items?.[index]?.quantity?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }} >
                    <Controller
                      name={`items.${index}.unitNet`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          label="Cena netto *"
                          fullWidth
                          inputProps={{ step: '0.01' }}
                          error={!!errors.items?.[index]?.unitNet}
                          helperText={errors.items?.[index]?.unitNet?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }} >
                    <Controller
                      name={`items.${index}.unitGross`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value || ''}
                          type="number"
                          label="Cena brutto"
                          fullWidth
                          inputProps={{ step: '0.01' }}
                          error={!!errors.items?.[index]?.unitGross}
                          helperText={errors.items?.[index]?.unitGross?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }} >
                    <Controller
                      name={`items.${index}.vatRate`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value || ''}
                          type="number"
                          label="Stawka VAT (0-1)"
                          fullWidth
                          inputProps={{ step: '0.01', min: 0, max: 1 }}
                          error={!!errors.items?.[index]?.vatRate}
                          helperText={errors.items?.[index]?.vatRate?.message || 'np. 0.23 dla 23%'}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }} >
                    <Controller
                      name={`items.${index}.isVatExempt`}
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox {...field} checked={field.value || false} />}
                          label="Zwolnione z VAT"
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }} >
                    <Controller
                      name={`items.${index}.vatExemptNote`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value || ''}
                          label="Nota zwolnienia VAT"
                          fullWidth
                          error={!!errors.items?.[index]?.vatExemptNote}
                          helperText={errors.items?.[index]?.vatExemptNote?.message}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}

            {fields.length === 0 && (
              <Alert severity="warning">
                Brak pozycji faktury. Dodaj przynajmniej jedną pozycję lub zatwierdź bez zmian.
              </Alert>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Additional Fields */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Dodatkowe informacje
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Waluta (ISO 4217)"
                      fullWidth
                      placeholder="PLN"
                      error={!!errors.currency}
                      helperText={errors.currency?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }} >
                <Controller
                  name="totalNet"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value.replace(',', '.'))}
                      type="text"
                      inputMode="decimal"
                      label="Suma netto"
                      fullWidth
                      error={!!errors.totalNet}
                      helperText={errors.totalNet?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }} >
                <Controller
                  name="totalVat"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value.replace(',', '.'))}
                      type="text"
                      inputMode="decimal"
                      label="Suma VAT"
                      fullWidth
                      error={!!errors.totalVat}
                      helperText={errors.totalVat?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }} >
                <Controller
                  name="totalGross"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value.replace(',', '.'))}
                      type="text"
                      inputMode="decimal"
                      label="Suma brutto"
                      fullWidth
                      error={!!errors.totalGross || lowConfidenceFieldMessages.has('totalGross')}
                      helperText={errors.totalGross?.message ?? lowConfidenceFieldMessages.get('totalGross')}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }} >
                <Controller
                  name="currencyRate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value.replace(',', '.'))}
                      type="text"
                      inputMode="decimal"
                      label="Kurs waluty"
                      fullWidth
                      error={!!errors.currencyRate}
                      helperText={errors.currencyRate?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="reviewNote"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Nota weryfikacyjna"
                      fullWidth
                      multiline
                      rows={2}
                      error={!!errors.reviewNote}
                      helperText={errors.reviewNote?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }} >
                <Controller
                  name="comments"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Komentarze"
                      fullWidth
                      multiline
                      rows={3}
                      error={!!errors.comments}
                      helperText={errors.comments?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }} >
                <Controller
                  name="legalNote"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Nota prawna"
                      fullWidth
                      multiline
                      rows={2}
                      error={!!errors.legalNote}
                      helperText={errors.legalNote?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Anuluj
        </Button>
        <Button
          variant="outlined"
          color="success"
          onClick={handleApproveWithoutCorrections}
          disabled={isLoading || !canApproveWithoutCorrections}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          Zatwierdź bez zmian
        </Button>
        <Button
          variant="contained"
          color="success"
          type="submit"
          form="approve-import-form"
          disabled={isLoading || !isApproveEnabled}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {isLoading ? 'Zatwierdzanie...' : 'Zatwierdź z korektami'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApproveImportModal;
